/***
 *
 *   STAT
 *   Statistic value with optional icon and -/+ change value.
 *   Ported from ui-reference/src/components/layouts/stat-card.tsx.
 *
 *   PROPS
 *   value: numeric value
 *   label: string
 *   loading: boolean to toggle loading animation (optional)
 *   icon: feather icon name (optional)
 *   iconColor: tint key — blue/purple/pink/green/red/orange/yellow (optional)
 *   change: signed number indicating change amount (optional)
 *   changeSuffix: unit rendered after the change, defaults to '%' (optional)
 *   lowerIsBetter: tint a rising change red instead of green — for durations,
 *     where "up" is a regression (optional)
 *   cardClass: LEGACY — extra classes on the card itself
 *
 **********/

import { Icon, Loader, ClassHelper, cn } from 'components/lib';
import Style, { ICON_TINTS } from './stat.tailwind.js';

export function Stat(props) {
    // `loadinbg` was a long-standing typo in the original and is kept so the
    // one call site that relies on it does not silently change behaviour.
    const isLoading = props.loading || props.loadinbg || props.value === undefined;

    const statStyle = ClassHelper(Style, {
        base: true,
        className: cn(props.cardClass, props.className),
    });

    if (isLoading) {
        return (
            <div className={statStyle}>
                <div className="flex h-10 items-center justify-center">
                    <Loader absoluteStyle={false} size={20} />
                </div>
            </div>
        );
    }

    // Read the sign off the number rather than off its string form: '0.00' has
    // no '-' in it and used to render as a green upward move.
    const changeValue = props.change == null || props.change === '' ? null : Number(props.change);
    const hasChange = changeValue !== null && Number.isFinite(changeValue);
    const changeUp = hasChange && changeValue > 0;
    const changeDown = hasChange && changeValue < 0;
    const isRegression = props.lowerIsBetter ? changeUp : changeDown;
    const changeSuffix = props.changeSuffix ?? '%';
    // keep whatever precision the caller formatted with — Math.abs() on the
    // number would turn '10.00' into '10'
    const changeText = hasChange ? String(props.change).replace('-', '') : '';

    return (
        <div className={statStyle}>
            <div className={Style.row}>
                {props.icon && (
                    <div className={cn(Style.iconWell, ICON_TINTS[props.iconColor] || ICON_TINTS.blue)}>
                        <Icon image={props.icon} size={18} color="currentColor" />
                    </div>
                )}

                <div className={Style.body}>
                    <p className={Style.label}>{props.label}</p>
                    <div className={Style.valueRow}>
                        <p className={Style.value}>
                            {props.value}
                            {props.valueLabel && (
                                <span className="ml-1 text-sm font-normal text-text-muted">{props.valueLabel}</span>
                            )}
                        </p>
                        {hasChange && (
                            <span
                                className={cn(
                                    Style.change,
                                    !changeUp && !changeDown
                                        ? Style.changeFlat
                                        : isRegression
                                          ? Style.changeDown
                                          : Style.changeUp,
                                )}>
                                {(changeUp || changeDown) && (
                                    <Icon
                                        image={changeUp ? 'trending-up' : 'trending-down'}
                                        size={12}
                                        color="currentColor"
                                    />
                                )}
                                {changeText}
                                {changeSuffix}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
